import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  CellContext,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table as TableType,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Store } from '../../../types/stores.types';
import Button from '../../Button/Button';
import { ButtonVariants } from '../../Button/Button.types';
import Checkbox from '../../Checkbox/Checkbox';
import Input from '../../Input/Input';
import inputStyles from '../../Input/Input.module.css';
import Tooltip from '../../Tooltip/Tooltip';
import sharedTableStyles from '../SharedTableStyles.module.css';
import SortIcon from '../SortIcon';
import styles from './TableCardGlobalSearch.module.css';
import { TableCardProps } from './TableCardGlobalSearch.types';

export const provideNewSelectedRows = (selectedRows: RowSelectionState | undefined, filteredData: any[]) => {
  const selectedStores = new Set(Object.keys(selectedRows as RowSelectionState));
  const newSelectedRows: any = {};
  filteredData.forEach((row) => {
    if (selectedStores.has(row.storeNo)) {
      newSelectedRows[row.storeNo] = true;
    }
  });
  return newSelectedRows;
};

const TableCardGlobalSearch = <TData extends object>({
  data = [],
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  noRowsMessage = 'No data to show',
  title,
  className,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  width,
  includeMinWidth = true,
  accessTableApi,
  setFilteredData,
}: TableCardProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});

  const [columnFilters, setColumnFilters] = useState<Array<{ id: string; value: any }>>([]);
  const [columnId, setColumnId] = useState<string>('fzCode');
  const [searchValue, setSearchValue] = useState('');

  const [tableData, setTableData] = useState(() => (isLoading ? Array(30).fill({}) : data));

  const iD = useId();

  const onFilterChange = (id: any, value: any) => {
    setColumnFilters((prev) => prev.filter((f) => f.id !== (columnId as string)).concat({ id, value }));
  };

  const handleButtonClick = (event: any, newId: any) => {
    if (newId && columnId !== newId) {
      setColumnId(newId);
    }
  };

  const filteredData: Array<any> = useMemo(() => {
    const currentColumn = columnId;
    const searchQuery = searchValue.toLowerCase();

    if (!searchQuery) {
      return data;
    }

    return data?.filter((row) => {
      if (currentColumn === 'storeNo' && (row as Store).storeNo?.toString().includes(searchQuery)) {
        return true;
      }
      if (currentColumn === 'fzCode' && (row as Store).fzCode?.toString().toLowerCase().includes(searchQuery)) {
        return true;
      }
      return false;
    });
  }, [columnId, searchValue, data]);

  const handleSelectedRows = () => {
    if (!searchValue) {
      onRowSelectionChange({});
      return;
    }
    const newSelectedRows = provideNewSelectedRows(selectedRows, filteredData);
    onRowSelectionChange(newSelectedRows);
  };

  const handleSearch = () => {
    setSearchValue(searchValue.toLowerCase());
    setFilteredData(filteredData);
    setTableData(filteredData);
    handleSelectedRows();
  };

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <div className={sharedTableStyles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${iD}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={params.row.getToggleSelectedHandler()}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [iD],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${iD}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [iD],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    return enableRowSelection ? columnsWithRowSelection : columns;
  }, [isLoading, enableRowSelection, columnsWithRowSelection, columns, LoadingSkeleton]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    getRowId: (row) => row.storeNo,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowCount);
    }
    return t('common.table.row', { count: filteredRowCount });
  }, [returnedRecordsText, filteredRowCount, t]);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(true);
    }
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  const toggleButtonStyles = {
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '18px',
    padding: '5px',
    width: '76px',
    textTransform: 'none',
  };

  const selectedToggleButtonStyles = {
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '18px',
    padding: '5px',
    width: '76px',
    textTransform: 'none',
    boxShadow: '3px 3px 6px 0px #13131340 inset',
  };

  const toggleButtonGroupStyles = {
    border: '1px solid var(--black-500)',
    borderRadius: '1rem',
    height: '37px',
  };

  return (
    <div
      style={{ width }}
      className={classNames(styles.card, {
        [className!]: className !== undefined,
        [sharedTableStyles.minWidth]: includeMinWidth,
      })}
    >
      <div
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.noTitle]: title === undefined,
          [styles.loading]: isLoading,
          [styles.withVerticalScrollBar]: hasVerticalScrollbar,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          <caption className={classNames(styles.firstTableTitle)}>
            <div className={styles.searchSection}>
              <div className={styles.toggleTitleAndToggles}>
                <div className={styles.toggleTitle}>{t('menu.filter.searchBy')}</div>

                <ToggleButtonGroup
                  value={columnId}
                  exclusive
                  onChange={(event, newId) => handleButtonClick(event, newId)}
                  aria-label="search-by toggle buttons"
                  color="primary"
                  sx={toggleButtonGroupStyles}
                >
                  <ToggleButton
                    value="storeNo"
                    data-testid="storeToggle"
                    aria-label={t('menu.filter.storeAria')}
                    sx={columnId === 'storeNo' ? selectedToggleButtonStyles : toggleButtonStyles}
                  >
                    {t('menu.filter.storeToggle')}
                  </ToggleButton>
                  <ToggleButton
                    value="fzCode"
                    data-testid="fzCodeToggle"
                    aria-label={t('menu.filter.fzAria')}
                    sx={columnId === 'fzCode' ? selectedToggleButtonStyles : toggleButtonStyles}
                  >
                    {t('menu.filter.fzToggle')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <div className={styles.searchTitleAndInput}>
                <div className={styles.searchTitle}>
                  {columnId === 'storeNo' ? (
                    'Store #'
                  ) : (
                    <span>
                      <Trans i18nKey="menu.filter.fzCode" components={[<span key="xxx" className={styles.xxx} />]} />
                    </span>
                  )}
                </div>

                <Input
                  label=""
                  id="searchInput"
                  value={searchValue}
                  className={styles.searchInput}
                  hideLabel
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <Button
                variant={ButtonVariants.Secondary}
                color="primary"
                className={styles.searchButton}
                onClick={handleSearch}
              >
                {t('common.search')}
              </Button>
            </div>
          </caption>

          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={classNames(sharedTableStyles.columnHeader, {
                      [styles.checkboxColumnHeader]: enableRowSelection && index === 0,
                    })}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                      tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                      aria-label={`sort column ${
                        typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                      }`}
                      onClick={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      onTouchStart={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      role="button"
                      onKeyDown={(event: KeyboardEvent) => {
                        switch (event.key) {
                          case ' ':
                          case 'Enter':
                            header.column.toggleSorting();
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span className={sharedTableStyles.columnHeaderText}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.columnDef.enableSorting && <SortIcon />}
                      {header.column.columnDef.meta?.tooltipText !== undefined && (
                        <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <input
                        disabled={header.column.columnDef.meta?.disableFilter}
                        onChange={(event) => header.column.setFilterValue(event.target.value)}
                        value={(header.column.getFilterValue() as string) ?? ''}
                        className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                        aria-label={`${header.column.columnDef.header} filter input`}
                        id={`${header.id}filterInput`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={sharedTableStyles.cell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {!isLoading && filteredRowCount === 0 && <caption className={styles.noRowsMessage}>{noRowsMessage}</caption>}
        </table>
      </div>
      <div className={styles.footer}>{!isLoading && <h2>{`${filteredRowCount} ${recordText}`}</h2>}</div>
      {hasHorizontalScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default TableCardGlobalSearch;
